import React, { useState } from 'react';
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert,
  Stack,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment,
} from '@mui/material';
import { Calculate, Info } from '@mui/icons-material';
import ResponsiveDiagram from './ResponsiveDiagram';

const CircRad: React.FC = () => {
  const [insideRun, setInsideRun] = useState('');
  const [outsideRun, setOutsideRun] = useState('');
  const [width, setWidth] = useState('');
  const [inside90, setInside90] = useState<string | null>(null);
  const [outside90, setOutside90] = useState<string | null>(null);
  const [insideAngle, setInsideAngle] = useState<string | null>(null);
  const [outsideAngle, setOutsideAngle] = useState<string | null>(null);
  const [insideRadius, setInsideRadius] = useState<string | null>(null);
  const [outsideRadius, setOutsideRadius] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [calculatedInputs, setCalculatedInputs] = useState<string | null>(null);
  const currentInputs = JSON.stringify([insideRun, outsideRun, width]);
  const isStale = calculatedInputs !== null && calculatedInputs !== currentInputs;

  const handleCalculate = () => {
    if (!insideRun || !outsideRun || !width) {
      setShowError(true);
      return;
    }

    const insideRunNum = parseFloat(insideRun);
    const outsideRunNum = parseFloat(outsideRun);
    const widthNum = parseFloat(width);

    if (isNaN(insideRunNum) || isNaN(outsideRunNum) || isNaN(widthNum)) {
      setShowError(true);
      return;
    }

    setShowError(false);
    setCalculatedInputs(currentInputs);

    // CircRad calculation logic from Android app - iterative approach
    let bestInside90 = 0;
    let bestInsideAngle = 0;
    let bestOutside90 = 0;
    let bestOutsideAngle = 0;
    let bestInsideRad = 0;
    let bestOutsideRad = 0;

    let prevDiff = 10;

    // Iterate through possible radius values to find the best match
    for (let testRad = 1; testRad <= 400; testRad += 0.25) {
      // Calculate inside measurements
      const tempI90 = ((testRad * Math.PI) / 2) / insideRunNum;
      const tempIa = 90 / tempI90;

      // Calculate outside measurements
      const tempO90 = (((testRad + widthNum) * Math.PI) / 2) / outsideRunNum;
      const tempOa = 90 / tempO90;

      // Find the configuration with the smallest angle difference
      const difference = Math.abs(tempOa - tempIa);
      
      if (difference < prevDiff) {
        bestInside90 = tempI90;
        bestOutside90 = tempO90;
        bestInsideAngle = tempIa;
        bestOutsideAngle = tempOa;
        bestInsideRad = testRad;
        bestOutsideRad = testRad + widthNum;

        prevDiff = difference;
      }
    }

    // Round to 2 decimal places
    const roundedInside90 = Math.round(bestInside90 * 100) / 100;
    const roundedOutside90 = Math.round(bestOutside90 * 100) / 100;
    const roundedInsideAngle = Math.round(bestInsideAngle * 100) / 100;
    const roundedOutsideAngle = Math.round(bestOutsideAngle * 100) / 100;
    const roundedInsideRad = Math.round(bestInsideRad * 100) / 100;
    const roundedOutsideRad = Math.round(bestOutsideRad * 100) / 100;

    setInside90(roundedInside90.toString());
    setOutside90(roundedOutside90.toString());
    setInsideAngle(roundedInsideAngle.toString());
    setOutsideAngle(roundedOutsideAngle.toString());
    setInsideRadius(roundedInsideRad.toString());
    setOutsideRadius(roundedOutsideRad.toString());
  };

  const hasResults = inside90 && outside90 && insideAngle && outsideAngle && insideRadius && outsideRadius;

  return (
    <Box component="main" id="main-content" tabIndex={-1} sx={{
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #F5F5F5 0%, #E8EAF6 100%)',
      py: { xs: 2, sm: 4 }
    }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Find Unknown Circular Radius
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Optimize radius for inside/outside measurements
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
          {/* Input Form */}
          <Card component="form" noValidate onSubmit={(event) => { event.preventDefault(); handleCalculate(); }} elevation={3} sx={{ flex: 1, minWidth: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Calculate sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Input Values
                </Typography>
              </Box>

              <Stack spacing={3}>
                {showError && (
                  <Alert severity="error">Enter a number in each highlighted field, then calculate again.</Alert>
                )}
                <TextField
                  fullWidth
                  label="Inside Run"
                  type="number"
                  value={insideRun}
                  error={showError && (!insideRun || isNaN(parseFloat(insideRun)))}
                  onChange={(e) => setInsideRun(e.target.value)}
                  inputProps={{ step: "0.01", inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Tread width on inside curve"
                />
                
                <TextField
                  fullWidth
                  label="Outside Run"
                  type="number"
                  value={outsideRun}
                  error={showError && (!outsideRun || isNaN(parseFloat(outsideRun)))}
                  onChange={(e) => setOutsideRun(e.target.value)}
                  inputProps={{ step: "0.01", inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Tread width on outside curve"
                />

                <TextField
                  fullWidth
                  label="Width"
                  type="number"
                  value={width}
                  error={showError && (!width || isNaN(parseFloat(width)))}
                  onChange={(e) => setWidth(e.target.value)}
                  inputProps={{ step: "0.01", inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Distance between inside and outside curves"
                />

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  type="submit"
                  startIcon={<Calculate />}
                  sx={{ 
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #1976D2 30%, #42A5F5 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #0D47A1 30%, #1976D2 90%)',
                    }
                  }}
                >
                  Calculate
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Results */}
          <Card component="section" aria-label="Calculation results" aria-live="polite" aria-atomic="true" elevation={3} sx={{ flex: 2 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Info sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Optimized Results
                </Typography>
              </Box>

              {isStale && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Inputs changed. These results are from your previous calculation. Calculate again to update them.
                </Alert>
              )}

              {hasResults ? (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                  gap: 2
                }}>
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="Inside Treads in 90°" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Inside Treads in 90\u00b0" }}
                      fullWidth
                      value={`${inside90} treads`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#eef7f0',
                          color: 'text.primary',
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="Outside Treads in 90°" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Outside Treads in 90\u00b0" }}
                      fullWidth
                      value={`${outside90} treads`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#eef7f0',
                          color: 'text.primary',
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="Inside Tread Angle" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Inside Tread Angle" }}
                      fullWidth
                      value={`${insideAngle}°`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#eef7f0',
                          color: 'text.primary',
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="Outside Tread Angle" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Outside Tread Angle" }}
                      fullWidth
                      value={`${outsideAngle}°`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#eef7f0',
                          color: 'text.primary',
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="Inside Radius" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Inside Radius" }}
                      fullWidth
                      value={`${insideRadius} inches`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#edf5fc',
                          color: 'text.primary',
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label="Outside Radius" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Outside Radius" }}
                      fullWidth
                      value={`${outsideRadius} inches`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#edf5fc',
                          color: 'text.primary',
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ gridColumn: '1 / -1', mt: 1, p: 2, bgcolor: '#fff4df', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.primary">
                      <strong>Algorithm:</strong> Iterative optimization (1-400 radius range, 0.25 increments) to minimize angle differences
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 6,
                  color: 'text.secondary'
                }}>
                  <Calculate sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1">
                    Enter all measurements to find optimized radius configuration
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Diagram */}
        <Card elevation={3} sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
              Reference Diagram
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <ResponsiveDiagram 
              imageName="circ_rad" 
              alt="Circular radius optimization diagram showing inside/outside run measurements" 
            />
          </CardContent>
        </Card>

      </Container>
    </Box>
  );
};

export default CircRad;